/**
 * Order Status Utility Functions
 * Centralizes status mapping, colors, and display logic for order components
 */

// Status category mappings
export const STATUS_CATEGORIES = {
  ORDER_CREATED: [
    "new_order",
    "orderPlaced_patientPayLater",
    "orderPlaced_notPatientPayLater",
    "orderPlaced_notPatientPayLater_textOnly"
  ],
  ORDER_MODIFIED: [
    "order_edit",
    "editOrder_testRemoval",
    "add_on_order"
  ],
  ORDER_CANCELED: [
    "order_canceled",
    "order_concierge_unpaid"
  ],
  ORDER_REDRAWN:[
    "order_redraw"
  ],
  PAYMENT_COMPLETE: [
    "order_payout_shipToPatient",
    "order_payout_suppliedByClient_blood",
    "order_payout_suppliedByClient_nonBlood",
    "order_payout_suppliedByClient_both"
  ],
  PATIENT_RECEIVED: [
    "Patient Received"
  ],
  TNP_ISSUE: [
    "TNP Issue"
  ],
  TNP_RESOLVED: [
    "TNP Resolved"
  ],
  REPORT_AVAILABLE: [
    "Report Available"
  ],
  QUESTIONNAIRE_ASSIGNED: [],
  QUESTIONNAIRE_COMPLETED: [],
};

// Status display text mapping
export const STATUS_DISPLAY_TEXT = {
  ORDER_CREATED: "Order Created",
  ORDER_MODIFIED: "Order Modified", 
  ORDER_CANCELED: "Order Canceled",
  ORDER_REDRAWN: "Order Redrawn",
  PAYMENT_COMPLETE: "Payment Complete",
  PATIENT_RECEIVED: "Patient Received",
  TNP_ISSUE: "TNP Issue",
  TNP_RESOLVED: "TNP Resolved",
  REPORT_AVAILABLE: "Report Available",
  QUESTIONNAIRE_ASSIGNED: "Questionnaire Assigned",
  QUESTIONNAIRE_COMPLETED: "Questionnaire Completed",
};

// Status color mapping
export const STATUS_COLORS = {
  ORDER_CREATED: "#f06539",
  ORDER_MODIFIED: "#f06539",
  ORDER_CANCELED: "#d10000",
  ORDER_REDRAWN: "#d10000",
  PAYMENT_COMPLETE: "#00a86b",
  PATIENT_RECEIVED: "#f06539",
  TNP_ISSUE: "#d10000",
  TNP_RESOLVED: "#25B8C2",
  REPORT_AVAILABLE: "#004879",
  QUESTIONNAIRE_ASSIGNED: "#25B8C2",
  QUESTIONNAIRE_COMPLETED: "#25B8C2"
};

// Status background color mapping
export const STATUS_BACKGROUND_COLORS = {
  ORDER_CREATED: "#ffeee6",
  ORDER_MODIFIED: "#ffeee6",
  ORDER_CANCELED: "#ffe6e6",
  ORDER_REDRAWN: "#ffe6e6",
  PAYMENT_COMPLETE: "#ebfff8",
  PATIENT_RECEIVED: "#ffeee6",
  TNP_ISSUE: "#ffe6e6",
  TNP_RESOLVED: "#ebfff8",
  REPORT_AVAILABLE: "#e5f1fb",
  QUESTIONNAIRE_ASSIGNED: "#EBFFF8",
  QUESTIONNAIRE_COMPLETED: "#EBFFF8"
};

// Status description mapping by category
export const STATUS_DESCRIPTIONS = {
  ORDER_CREATED: "The order has been successfully created in the system.",
  ORDER_REDRAWN: "The sample was redrawn due to insufficient or compromised initial collection.",
  ORDER_CANCELED: "The order has been canceled and will not proceed further.",
  ORDER_MODIFIED: "The order details have been updated by the provider or system.",
  QUESTIONNAIRE_ASSIGNED: "A questionnaire has been assigned to the patient for completion.",
  QUESTIONNAIRE_COMPLETED: "The patient has completed the assigned questionnaire.",
};

// Default description for statuses not specifically mapped
export const DEFAULT_STATUS_DESCRIPTION = "Order Status is updated";

/**
 * Get the description for a status
 * @param {string} status - The status code or name
 * @returns {string} - The status description or default message
 */
export function getStatusDescription(status) {
  const category = getStatusCategory(status);
  return category ? STATUS_DESCRIPTIONS[category] || DEFAULT_STATUS_DESCRIPTION : DEFAULT_STATUS_DESCRIPTION;
}

/**
 * Get the category of a status
 * @param {string} status - The status code or name
 * @returns {string|null} - The category name or null if not found
 */
export function getStatusCategory(status) {
  for (const [category, statuses] of Object.entries(STATUS_CATEGORIES)) {
    if (statuses.includes(status)) {
      return category;
    }
  }
  return null;
}

/**
 * Get the display text for a status
 * @param {string} status - The status code or name
 * @returns {string} - The display text for the status
 */
export function getStatusDisplayText(status) {
  const category = getStatusCategory(status);
  return category ? STATUS_DISPLAY_TEXT[category] : "Status Update";
}

/**
 * Get the color for a status
 * @param {string} status - The status code or name
 * @returns {string} - The hex color code
 */
export function getStatusColor(status) {
  const category = getStatusCategory(status);
  return category ? STATUS_COLORS[category] : "#333333";
}

/**
 * Get the background color for a status
 * @param {string} status - The status code or name
 * @returns {string} - The hex background color code
 */
export function getStatusBackgroundColor(status) {
  const category = getStatusCategory(status);
  return category ? STATUS_BACKGROUND_COLORS[category] : "#e0e0e0";
}

/**
 * Get complete status info object
 * @param {string} status - The status code or name
 * @returns {object} - Complete status information
 */
export function getStatusInfo(status) {
  const category = getStatusCategory(status);
  
  return {
    originalStatus: status,
    category: category,
    displayText: getStatusDisplayText(status),
    color: getStatusColor(status),
    backgroundColor: getStatusBackgroundColor(status),
    isValid: category !== null
  };
}

/**
 * Check if a status is in a specific category
 * @param {string} status - The status code or name
 * @param {string} categoryName - The category to check against
 * @returns {boolean} - True if status is in the category
 */
export function isStatusInCategory(status, categoryName) {
  const category = getStatusCategory(status);
  return category === categoryName;
}

/**
 * Get all statuses in a category
 * @param {string} categoryName - The category name
 * @returns {array} - Array of status codes in the category
 */
export function getStatusesInCategory(categoryName) {
  return STATUS_CATEGORIES[categoryName] || [];
}

// Default export with all functions
export default {
  STATUS_CATEGORIES,
  STATUS_DISPLAY_TEXT,
  STATUS_COLORS,
  STATUS_BACKGROUND_COLORS,
  getStatusCategory,
  getStatusDisplayText,
  getStatusColor,
  getStatusBackgroundColor,
  getStatusInfo,
  isStatusInCategory,
  getStatusesInCategory
};
